import React, { useState, useEffect } from 'react';
import { Quest, Tag } from '../../types/auth';
import authService from '../../services/authService';
import './QuestModal.css';

interface QuestModalProps {
  quest?: Quest | null;
  isOpen: boolean;
  onSave: (questData: Quest) => void;
  onClose: () => void;
}

const QuestModal: React.FC<QuestModalProps> = ({
  quest,
  isOpen,
  onSave,
  onClose,
}) => {
  const [formData, setFormData] = useState<Quest>({
    id: '',
    title: '',
    subtitle: '',
    description: '',
    image_url: '',
    video_url: '',
    thumb_url: '',
    marketing_url: '',
    subscription_image: '',
    subscription_title: '',
    subscription_subtitle: '',
    share_image_url: '',
    share_text: '',
    duration: 0,
    no_of_modules: 0,
    no_of_exercises: 0,
    no_of_videos: 0,
    no_of_audios: 0,
    total_no_of_posts: 0,
    success_image_url: '',
    title_color: '',
    landscape_image_url: '',
    portrait_image_url: '',
    creator_id: '',
    is_public: true,
    tags: [],
    category: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);

  const categoryMap: Record<string, string> = {
    devotional: 'Devotional',
    'self-help': 'Self Help',
    health: 'Health',
  };

  const categories = Object.keys(categoryMap);

  useEffect(() => {
    if (isOpen) {
      fetchTags();
    }
    if (quest) {
      setFormData({
        ...quest,
        thumb_url: quest.thumb_url || '',
        marketing_url: quest.marketing_url || '',
        subscription_image: quest.subscription_image || '',
        subscription_title: quest.subscription_title || '',
        subscription_subtitle: quest.subscription_subtitle || '',
        share_image_url: quest.share_image_url || '',
        share_text: quest.share_text || '',
        no_of_exercises: quest.no_of_exercises || 0,
        no_of_videos: quest.no_of_videos || 0,
        no_of_audios: quest.no_of_audios || 0,
        total_no_of_posts: quest.total_no_of_posts || 0,
        success_image_url: quest.success_image_url || '',
        title_color: quest.title_color || '',
        landscape_image_url: quest.landscape_image_url || '',
        portrait_image_url: quest.portrait_image_url || '',
        tags: quest.tags || [],
        category: quest.category || '',
      });
    } else {
      setFormData({
        id: '',
        title: '',
        subtitle: '',
        description: '',
        image_url: '',
        video_url: '',
        thumb_url: '',
        marketing_url: '',
        subscription_image: '',
        subscription_title: '',
        subscription_subtitle: '',
        share_image_url: '',
        share_text: '',
        duration: 0,
        no_of_modules: 0,
        no_of_exercises: 0,
        no_of_videos: 0,
        no_of_audios: 0,
        total_no_of_posts: 0,
        success_image_url: '',
        title_color: '',
        landscape_image_url: '',
        portrait_image_url: '',
        creator_id: '',
        is_public: true,
        tags: [],
        category: '',
      });
    }
    setErrors({});
  }, [quest, isOpen]);

  const fetchTags = async () => {
    try {
      setLoadingTags(true);
      const response = await authService.getTags();
      setAvailableTags(response.tags || []);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    } finally {
      setLoadingTags(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.image_url.trim()) {
      newErrors.image_url = 'Image URL is required';
    }

    if (formData.duration <= 0) {
      newErrors.duration = 'Duration must be greater than 0';
    }

    if (formData.no_of_modules <= 0) {
      newErrors.no_of_modules = 'Number of modules must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Prepare the data for API (ensure all fields are properly handled)
      const questDataToSave = {
        ...formData,
        video_url: formData.video_url || '', // Ensure empty string if not provided
        subtitle: formData.subtitle || '',
        description: formData.description || '',
        thumb_url: formData.thumb_url || '',
        marketing_url: formData.marketing_url || '',
        subscription_image: formData.subscription_image || '',
        subscription_title: formData.subscription_title || '',
        subscription_subtitle: formData.subscription_subtitle || '',
        share_image_url: formData.share_image_url || '',
        share_text: formData.share_text || '',
        no_of_exercises: formData.no_of_exercises || 0,
        no_of_videos: formData.no_of_videos || 0,
        no_of_audios: formData.no_of_audios || 0,
        total_no_of_posts: formData.total_no_of_posts || 0,
        success_image_url: formData.success_image_url || '',
        title_color: formData.title_color || '',
        landscape_image_url: formData.landscape_image_url || '',
        portrait_image_url: formData.portrait_image_url || '',
        is_public: formData.is_public,
        tags: formData.tags || [],
        category: formData.category || '',
      };

      console.log('Form data being saved:', questDataToSave);

      // For create mode, we need to exclude id and creator_id
      if (!quest) {
        const { id, creator_id, ...createData } = questDataToSave;
        console.log('Saving new quest with data:', createData);
        await onSave(createData as Quest);
      } else {
        // For edit mode, pass the full quest data
        console.log('Saving edited quest with data:', questDataToSave);
        await onSave(questDataToSave);
      }

      onClose();
    } catch (error) {
      console.error('Error saving quest:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? checked
          : name === 'duration' || name === 'no_of_modules'
            ? parseInt(value) || 0
            : value,
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleTagsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedIds = Array.from(
      e.target.selectedOptions,
      option => option.value
    );
    const selectedSlugs = availableTags
      .filter(tag => selectedIds.includes(tag.id))
      .map(tag => tag.slug);
    setFormData(prev => ({ ...prev, tags: selectedSlugs }));
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2 className="modal-title">
            {quest ? 'Edit Quest' : 'Create New Quest'}
          </h2>
          <button className="modal-close" onClick={onClose}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label htmlFor="title" className="form-label">
              Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className={`form-input ${errors.title ? 'error' : ''}`}
              placeholder="Enter quest title"
            />
            {errors.title && (
              <span className="error-message">{errors.title}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="subtitle" className="form-label">
              Subtitle
            </label>
            <input
              type="text"
              id="subtitle"
              name="subtitle"
              value={formData.subtitle}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Enter quest subtitle"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description" className="form-label">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className="form-textarea"
              placeholder="Enter quest description"
              rows={4}
            />
          </div>

          <div className="form-group">
            <label htmlFor="category" className="form-label">
              Category
            </label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              className="form-input"
            >
              <option value="">Select a category</option>
              {categories.map(slug => (
                <option key={slug} value={slug}>
                  {categoryMap[slug]}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="tags" className="form-label">
              Tags {loadingTags && '(Loading...)'}
            </label>
            <select
              id="tags"
              name="tags"
              multiple
              value={availableTags
                .filter(tag => formData.tags?.includes(tag.slug))
                .map(tag => tag.id)}
              onChange={handleTagsChange}
              className="form-select"
              size={5}
            >
              {availableTags.map(tag => (
                <option key={tag.id} value={tag.id}>
                  {tag.tag_name}
                </option>
              ))}
            </select>
            <span className="form-hint">
              Hold Ctrl/Cmd to select multiple tags
            </span>
          </div>

          <div className="form-group">
            <div className="checkbox-group">
              <input
                type="checkbox"
                id="is_public"
                name="is_public"
                checked={formData.is_public}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <label htmlFor="is_public" className="checkbox-label">
                Make this quest public
              </label>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="image_url" className="form-label">
              Image URL *
            </label>
            <input
              type="url"
              id="image_url"
              name="image_url"
              value={formData.image_url}
              onChange={handleInputChange}
              className={`form-input ${errors.image_url ? 'error' : ''}`}
              placeholder="Enter image URL"
            />
            {errors.image_url && (
              <span className="error-message">{errors.image_url}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="video_url" className="form-label">
              Video URL
            </label>
            <input
              type="url"
              id="video_url"
              name="video_url"
              value={formData.video_url}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Enter video URL"
            />
          </div>

          <div className="form-group">
            <label htmlFor="thumb_url" className="form-label">
              Thumbnail URL
            </label>
            <input
              type="url"
              id="thumb_url"
              name="thumb_url"
              value={formData.thumb_url || ''}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Enter thumbnail URL"
            />
          </div>

          <div className="form-group">
            <label htmlFor="marketing_url" className="form-label">
              Marketing URL
            </label>
            <input
              type="url"
              id="marketing_url"
              name="marketing_url"
              value={formData.marketing_url || ''}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Enter marketing URL"
            />
          </div>

          <div className="form-group">
            <label htmlFor="subscription_image" className="form-label">
              Subscription Image URL
            </label>
            <input
              type="url"
              id="subscription_image"
              name="subscription_image"
              value={formData.subscription_image || ''}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Enter subscription image URL"
            />
          </div>

          <div className="form-group">
            <label htmlFor="subscription_title" className="form-label">
              Subscription Title
            </label>
            <input
              type="text"
              id="subscription_title"
              name="subscription_title"
              value={formData.subscription_title || ''}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Enter subscription title"
            />
          </div>

          <div className="form-group">
            <label htmlFor="subscription_subtitle" className="form-label">
              Subscription Subtitle
            </label>
            <input
              type="text"
              id="subscription_subtitle"
              name="subscription_subtitle"
              value={formData.subscription_subtitle || ''}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Enter subscription subtitle"
            />
          </div>

          <div className="form-group">
            <label htmlFor="share_image_url" className="form-label">
              Share Image URL
            </label>
            <input
              type="url"
              id="share_image_url"
              name="share_image_url"
              value={formData.share_image_url || ''}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Enter share image URL"
            />
          </div>

          <div className="form-group">
            <label htmlFor="share_text" className="form-label">
              Share Text
            </label>
            <textarea
              id="share_text"
              name="share_text"
              value={formData.share_text || ''}
              onChange={handleInputChange}
              className="form-textarea"
              placeholder="Enter text for sharing"
              rows={3}
            />
          </div>

          <div className="form-group">
            <label htmlFor="success_image_url" className="form-label">
              Success Image URL
            </label>
            <input
              type="url"
              id="success_image_url"
              name="success_image_url"
              value={formData.success_image_url || ''}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Enter success completion image URL"
            />
          </div>

          <div className="form-group">
            <label htmlFor="title_color" className="form-label">
              Title Color
            </label>
            <input
              type="text"
              id="title_color"
              name="title_color"
              value={formData.title_color || ''}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Enter title color "
            />
          </div>

          <div className="form-group">
            <label htmlFor="landscape_image_url" className="form-label">
              Landscape Image URL
            </label>
            <input
              type="url"
              id="landscape_image_url"
              name="landscape_image_url"
              value={formData.landscape_image_url || ''}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Enter landscape image URL"
            />
          </div>

          <div className="form-group">
            <label htmlFor="portrait_image_url" className="form-label">
              Portrait Image URL
            </label>
            <input
              type="url"
              id="portrait_image_url"
              name="portrait_image_url"
              value={formData.portrait_image_url || ''}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Enter portrait image URL"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="no_of_exercises" className="form-label">
                Number of Exercises
              </label>
              <input
                type="number"
                id="no_of_exercises"
                name="no_of_exercises"
                value={formData.no_of_exercises || 0}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter number of exercises"
                min="0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="no_of_videos" className="form-label">
                Number of Videos
              </label>
              <input
                type="number"
                id="no_of_videos"
                name="no_of_videos"
                value={formData.no_of_videos || 0}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter number of videos"
                min="0"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="no_of_audios" className="form-label">
                Number of Audios
              </label>
              <input
                type="number"
                id="no_of_audios"
                name="no_of_audios"
                value={formData.no_of_audios || 0}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter number of audio files"
                min="0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="total_no_of_posts" className="form-label">
                Total Number of Posts
              </label>
              <input
                type="number"
                id="total_no_of_posts"
                name="total_no_of_posts"
                value={formData.total_no_of_posts || 0}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter total number of posts"
                min="0"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="duration" className="form-label">
                Duration (minutes) *
              </label>
              <input
                type="number"
                id="duration"
                name="duration"
                value={formData.duration}
                onChange={handleInputChange}
                className={`form-input ${errors.duration ? 'error' : ''}`}
                placeholder="Enter duration"
                min="1"
              />
              {errors.duration && (
                <span className="error-message">{errors.duration}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="no_of_modules" className="form-label">
                Number of Modules *
              </label>
              <input
                type="number"
                id="no_of_modules"
                name="no_of_modules"
                value={formData.no_of_modules}
                onChange={handleInputChange}
                className={`form-input ${errors.no_of_modules ? 'error' : ''}`}
                placeholder="Enter number of modules"
                min="1"
              />
              {errors.no_of_modules && (
                <span className="error-message">{errors.no_of_modules}</span>
              )}
            </div>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuestModal;
